import { useEffect, useMemo, useRef, useState } from 'react';
import './retro.css';

const KONAMI_SEQUENCE = ['up', 'up', 'down', 'down', 'left', 'right', 'left', 'right', 'b', 'a'];

const normalizeInput = (key, expected) => {
  const lower = key.toLowerCase();
  if (lower === 'arrowup' || lower === 'w') return 'up';
  if (lower === 'arrowdown' || lower === 's') return 'down';
  if (lower === 'arrowleft' || (lower === 'a' && expected !== 'a')) return 'left';
  if (lower === 'arrowright' || lower === 'd') return 'right';
  if (lower === 'b') return 'b';
  if (lower === 'a' && expected === 'a') return 'a';
  return null;
};

const RetroMode = ({ onToggle }) => {
  const [enabled, setEnabled] = useState(false);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [showGameOver, setShowGameOver] = useState(false);
  const [gameOverExiting, setGameOverExiting] = useState(false);
  const prevEnabledRef = useRef(false);
  const skipExitFadeRef = useRef(false);
  const exitHandledRef = useRef(false);
  const exitFlashTimeoutRef = useRef(null);

  const expected = useMemo(() => KONAMI_SEQUENCE[index], [index]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (enabled || loading) {
          skipExitFadeRef.current = true;
          exitHandledRef.current = true;
          document.body.classList.remove('retro-mode');
          document.body.classList.remove('retro-exit');
          document.body.classList.add('retro-exit-flash');
          document.body.classList.add('retro-exit-blackout');
          setEnabled(false);
          setLoading(false);
          setShowGameOver(false);
          setGameOverExiting(false);
          if (exitFlashTimeoutRef.current) {
            window.clearTimeout(exitFlashTimeoutRef.current);
          }
          exitFlashTimeoutRef.current = window.setTimeout(() => {
            document.body.classList.remove('retro-exit-flash');
            setShowGameOver(true);
            exitFlashTimeoutRef.current = null;
          }, 1000);
        }
        setIndex(0);
        return;
      }

      const input = normalizeInput(event.key, expected);
      if (!input) return;

      if (input === expected) {
        const nextIndex = index + 1;
        if (nextIndex === KONAMI_SEQUENCE.length) {
          setEnabled(true);
          setIndex(0);
          setLoading(true);
        } else {
          setIndex(nextIndex);
        }
      } else {
        setIndex(input === KONAMI_SEQUENCE[0] ? 1 : 0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, expected, index, loading]);

  useEffect(() => {
    const body = document.body;
    if (enabled) body.classList.add('retro-mode');
    else body.classList.remove('retro-mode');
    onToggle?.(enabled);
    if (enabled) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    return () => body.classList.remove('retro-mode');
  }, [enabled, onToggle]);

  useEffect(() => {
    const wasEnabled = prevEnabledRef.current;
    if (wasEnabled && !enabled) {
      if (exitHandledRef.current) {
        exitHandledRef.current = false;
        return undefined;
      }
      if (!skipExitFadeRef.current) {
        document.body.classList.add('retro-exit');
      }
      setShowGameOver(true);
      skipExitFadeRef.current = false;
      return undefined;
    }
    prevEnabledRef.current = enabled;
    return undefined;
  }, [enabled]);

  useEffect(() => {
    const shouldTrigger = localStorage.getItem('retroEasterEgg') === '1';
    if (shouldTrigger) {
      localStorage.removeItem('retroEasterEgg');
      setEnabled(true);
      setLoading(true);
    }
  }, []);

  useEffect(() => {
    if (!loading) return undefined;
    const start = performance.now();
    const duration = 1500;
    let frameId;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setLoadingPercent(Math.round(progress * 100));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    setLoadingPercent(0);
    frameId = requestAnimationFrame(tick);
    const id = window.setTimeout(() => setLoading(false), duration);

    return () => {
      window.clearTimeout(id);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [loading]);

  if (loading) {
    return (
      <div className="retro-loading" role="status" aria-live="polite">
        <div className="retro-loading__panel">
          <div className="retro-loading__title">ALUREN MISSION CONTROL</div>
          <div className="retro-loading__bar-row">
            <div className="retro-loading__bar">
              <div className="retro-loading__bar-fill" style={{ width: `${loadingPercent}%` }} />
            </div>
            <div className="retro-loading__percent">{loadingPercent}%</div>
          </div>
          <div className="retro-loading__subtitle">INITIALIZING RETRO SYSTEMS</div>
        </div>
      </div>
    );
  }

  if (showGameOver) {
    return (
      <div
        className={`retro-game-over${gameOverExiting ? ' retro-game-over--exit' : ''}`}
        role="dialog"
        aria-live="polite"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/game%20over.png)` }}
      >
        <div className="retro-game-over__stars" aria-hidden="true" />
        <div className="retro-game-over__planet" aria-hidden="true" />
        <div className="retro-game-over__planet retro-game-over__planet--small" aria-hidden="true" />
        <div className="retro-game-over__ufo" aria-hidden="true" />
        <div className="retro-game-over__panel">
          <div className="retro-game-over__title">GAME OVER</div>
          <button
            type="button"
            className="retro-game-over__button"
            onClick={() => {
              setGameOverExiting(true);
              window.setTimeout(() => {
                setShowGameOver(false);
                setGameOverExiting(false);
                document.body.classList.remove('retro-exit');
                document.body.classList.remove('retro-exit-flash');
                document.body.classList.remove('retro-exit-blackout');
              }, 800);
            }}
          >
            Return To Aluren Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default RetroMode;
